import { readStudentUserData } from "./studentMobileAccess";

/**
 * The student panel's basket: several mock exams, past papers and packs picked up
 * across the catalogue pages and paid for in one go.
 *
 * It lives in localStorage rather than the server `carts` table on purpose. The
 * table is the public website's basket, and a pick made inside the panel showing
 * up on 11plusmagic.com would surprise the buyer. Nothing here is money — the
 * prices are re-resolved server side at checkout — so the browser is a fine place
 * to keep it.
 */

const STORAGE_KEY = "panelBasket";

/** Signed-in student id, so one student's basket never greets the next one. */
function currentUserId() {
  const data = readStudentUserData();
  const id = data?.id ?? data?.user_id ?? null;
  return id == null ? null : String(id);
}

/**
 * Ids arrive as numbers from the catalogue and as JSON numbers from a checkout
 * refusal, but a stale basket can hold strings — compare them the same way, or a
 * removal quietly matches nothing.
 */
function lineKey(item) {
  return `${item.resourceType}:${Number(item.resourceId)}`;
}

/**
 * @typedef {object} PanelBasketItem
 * @property {"mock_exam"|"paper"|"bundle"} resourceType
 * @property {number} resourceId
 * @property {string} name
 * @property {number} price        numeric, for the total
 * @property {string} priceDisplay already formatted by the API, e.g. "£0.30"
 */

/** @returns {PanelBasketItem[]} */
export function readPanelBasket() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return [];
    // A basket left by a different student is not this student's to pay for.
    if (parsed.userId !== currentUserId()) return [];
    return parsed.items.filter((i) => i && i.resourceType && i.resourceId);
  } catch {
    return [];
  }
}

function write(items) {
  const userId = currentUserId();

  // Writing under a null id produces a basket no later read can ever match, because
  // readPanelBasket() compares against the id that has landed by then — the items are
  // on disk but unreachable, which looks exactly like an empty basket. Keep the
  // in-memory list and skip the write instead.
  if (userId == null) {
    window.dispatchEvent(new Event("panelBasketUpdated"));
    return items;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, items }));
  } catch {
    /* quota or private mode: the basket just will not survive a reload */
  }
  window.dispatchEvent(new Event("panelBasketUpdated"));
  return items;
}

/** Adding the same thing twice is a no-op, not a second copy. */
export function addToPanelBasket(item) {
  const items = readPanelBasket();
  if (items.some((row) => lineKey(row) === lineKey(item))) return items;
  return write([...items, item]);
}

export function removeFromPanelBasket(item) {
  return write(readPanelBasket().filter((row) => lineKey(row) !== lineKey(item)));
}

export function clearPanelBasket() {
  return write([]);
}

export function panelBasketHas(resourceType, resourceId) {
  return readPanelBasket().some(
    (row) => row.resourceType === resourceType && Number(row.resourceId) === Number(resourceId)
  );
}

export function panelBasketTotal(items = readPanelBasket()) {
  return items.reduce((sum, row) => sum + (Number(row.price) || 0), 0);
}

/**
 * Currency symbol to print the total in, taken from the items themselves so it
 * follows whatever the API formatted rather than being hardcoded here.
 */
export function panelBasketCurrency(items = readPanelBasket()) {
  const withSymbol = items.find((row) => /^\s*\D/.test(String(row.priceDisplay || "")));
  const match = String(withSymbol?.priceDisplay || "").match(/^\s*(\D+)/);
  return match ? match[1].trim() : "£";
}
